import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePageTitle, useHeaderActionsTarget } from '@/layouts/usePageTitle';
import { createPortal } from 'react-dom';
import { AgGridReact } from 'ag-grid-react';
import type { CellClickedEvent, ColDef, ColGroupDef, GridReadyEvent } from 'ag-grid-community';
import { AllEnterpriseModule, themeQuartz, colorSchemeLightCold } from 'ag-grid-enterprise';
import Highcharts from 'highcharts';
import {
  Plus,
  X,
  BarChart3,
  Trash2,
  Database,
  RefreshCw,
  Settings,
  CalendarDays,
  Factory,
  Plug,
  CheckCircle2,
  XCircle,
  Activity,
  Gauge,
  Zap,
  Thermometer,
  Radio,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import SiteEkkSettings from './SiteEkkSettings';
import PowerMeterPanel from '@/components/meters/PowerMeterPanel';
import toast from 'react-hot-toast';
import DropdownMenu from '@/ui/common/DropdownMenu';
import GridboxCoverageModal from './GridboxCoverageModal';
import { confirmToast, useErrorToast } from '@/lib/notify';
import {
  fetchSites,
  createSite,
  updateSite,
  deleteSite,
  fetchSiteGridboxArchive,
  fetchSiteGridboxEndexArchive,
  fetchSiteProductionSeries,
  testSiteGridboxConnection,
  fetchSitePlants,
  fetchAllPlantsForSelection,
  assignPlantsToSite,
  removePlantFromSite,
  fetchSiteEkkCurrent,
  fetchSiteEkkHistory,
} from '@/api/sites';
import type {
  GridboxArchiveRow,
  GridboxEndexRow,
  SiteProductionSeriesRow,
  Site,
  SitePlant,
  PlantForSelection,
  SiteEkkCurrentRow,
  SiteEkkHistoryRow,
} from '@/api/sites';
type GridboxRecord = Record<string, unknown>;

function extractErrorMessage(error: unknown): string {
  if (!error) return 'Bilinmeyen hata';
  if (typeof error === 'string') return error;

  const maybe = error as {
    response?: { data?: { message?: string; error?: string } | string; status?: number; statusText?: string };
    message?: string;
  };

  const responseData = maybe.response?.data;
  if (typeof responseData === 'string' && responseData.trim()) return responseData;
  if (responseData && typeof responseData === 'object') {
    if (typeof responseData.error === 'string' && responseData.error.trim()) return responseData.error;
    if (typeof responseData.message === 'string' && responseData.message.trim()) return responseData.message;
    try {
      const serialized = JSON.stringify(responseData);
      if (serialized && serialized !== '{}') return serialized;
    } catch {
      // no-op
    }
  }

  if (typeof maybe.response?.status === 'number') {
    return `${maybe.response.status} ${maybe.response.statusText || ''}`.trim() || String(maybe.response.status);
  }
  if (error instanceof Error && error.message) return error.message;
  if (typeof maybe.message === 'string' && maybe.message.trim()) return maybe.message;
  return 'Bilinmeyen hata';
}

const enterpriseModules = [AllEnterpriseModule];

const gridTheme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 11.5,
  rowHeight: 32,
  headerHeight: 32,
  borderRadius: 6,
  headerBackgroundColor: '#f8fafc',
  borderColor: '#e2e8f0',
  oddRowBackgroundColor: '#f8fafc',
});


type EnergyUnit = 'kWh' | 'MWh' | 'GWh';
function energyUnitFactor(unit: EnergyUnit): number {
  if (unit === 'GWh') return 1_000_000;
  if (unit === 'MWh') return 1_000;
  return 1;
}

function energyDigits(unit: EnergyUnit): number {
  return unit === 'kWh' ? 0 : 2;
}

function formatEnergyValue(value: unknown, unit: EnergyUnit, digits = energyDigits(unit)): string {
  const numeric = toNumber(value);
  if (numeric === null) return '—';
  const converted = numeric / energyUnitFactor(unit);
  return converted.toLocaleString('tr-TR', { maximumFractionDigits: digits });
}

function formatEnergyValueWithUnit(value: unknown, unit: EnergyUnit, digits = energyDigits(unit)): string {
  const formatted = formatEnergyValue(value, unit, digits);
  return formatted === '—' ? formatted : `${formatted} ${unit}`;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

function formatDateUTC(date: Date): string {
  return date.toISOString().slice(0, 10);
}

type GridboxBucket = 'hour' | 'day' | 'month' | 'year';

type DrillSelection = {
  bucket: GridboxBucket;
  year?: number;
  month?: number;
  day?: number;
};

type EnergySeriesPoint = {
  key: string;
  label: string;
  startAt: string;
  production: number;
  cumulativeProduction: number;
  consumption: number | null;
  cumulativeConsumption: number | null;
  outgoing: number | null;
  cumulativeOutgoing: number | null;
  hasOsosData: boolean;
  selfConsumption: number | null;
  cumulativeSelfConsumption: number | null;
  net: number | null;
};

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function buildProductionSeriesPoints(rows: SiteProductionSeriesRow[]): EnergySeriesPoint[] {
  let cumulativeProduction = 0;

  return rows
    .slice()
    .sort((a, b) => Number(a.periodIndex) - Number(b.periodIndex))
    .map((row) => {
      const production = Number(row.production) || 0;
      cumulativeProduction += production;
      return {
        key: String(row.periodIndex),
        label: row.label,
        startAt: row.startAt,
        production,
        cumulativeProduction,
        consumption: null,
        cumulativeConsumption: null,
        outgoing: null,
        cumulativeOutgoing: null,
        hasOsosData: false,
        selfConsumption: null,
        cumulativeSelfConsumption: null,
        net: null,
      };
    });
}

function getAnyField(row: GridboxRecord, candidates: string[]): unknown {
  const payload = row.payload && typeof row.payload === 'object' && !Array.isArray(row.payload)
    ? row.payload as GridboxRecord
    : null;
  for (const candidate of candidates) {
    if (candidate in row) return row[candidate];
    if (payload && candidate in payload) return payload[candidate];
  }
  return undefined;
}

function normalizeGridboxCandidateValue(candidate: string, value: unknown): number {
  const numeric = toNumber(value);
  if (numeric === null) return 0;
  void candidate;
  return numeric;
}

function getGridboxPeriodValue(row: GridboxRecord, candidates: string[]): number {
  return getNullableGridboxPeriodValue(row, candidates) ?? 0;
}

function getNullableGridboxPeriodValue(row: GridboxRecord, candidates: string[]): number | null {
  for (const candidate of candidates) {
    const value = getAnyField(row, [candidate]);
    const numeric = toNumber(value);
    if (numeric === null) continue;
    return normalizeGridboxCandidateValue(candidate, numeric);
  }

  return null;
}

function sumAvailable(left: number | null, right: number | null): number | null {
  return left === null && right === null ? null : (left ?? 0) + (right ?? 0);
}

function parseGridboxDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function inferGridboxDiffMultiplier(rows: Array<{ row: GridboxRecord }>): number {
  const ratios: number[] = [];
  for (let i = 1; i < rows.length; i += 1) {
    const prev = rows[i - 1].row;
    const curr = rows[i].row;

    const outDiff = getGridboxPeriodValue(curr, ['ActiveEndexOutDiff']);
    const outWithMultiplier = getGridboxPeriodValue(curr, ['ActiveEndexOutWithMultiplier']);
    const prevOutWithMultiplier = getGridboxPeriodValue(prev, ['ActiveEndexOutWithMultiplier']);
    const outDelta = outWithMultiplier - prevOutWithMultiplier;
    if (outDiff > 0 && outDelta > 0) {
      ratios.push(outDelta / outDiff);
    }

    const inDiff = getGridboxPeriodValue(curr, ['ActiveEndexDifference']);
    const inWithMultiplier = getGridboxPeriodValue(curr, ['ActiveEndexWithMultiplier']);
    const prevInWithMultiplier = getGridboxPeriodValue(prev, ['ActiveEndexWithMultiplier']);
    const inDelta = inWithMultiplier - prevInWithMultiplier;
    if (inDiff > 0 && inDelta > 0) {
      ratios.push(inDelta / inDiff);
    }
  }

  if (ratios.length === 0) return 1;
  const sorted = ratios.slice().sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  if (!Number.isFinite(median) || median <= 0) return 1;
  return median;
}

function inferGridboxDiffMultiplierFromArchiveRows(rows: GridboxArchiveRow[]): number {
  const sorted = rows
    .map((row) => {
      const date = parseGridboxDate(row.ProfileDate);
      if (!date) return null;
      return { date, row: row as unknown as GridboxRecord };
    })
    .filter((item): item is { date: Date; row: GridboxRecord } => Boolean(item))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
  return inferGridboxDiffMultiplier(sorted);
}

function bucketStart(date: Date, bucket: GridboxBucket): Date {
  const next = new Date(date);
  if (bucket === 'hour') {
    next.setMinutes(0, 0, 0);
    return next;
  }
  if (bucket === 'day') {
    next.setHours(0, 0, 0, 0);
    return next;
  }
  if (bucket === 'month') {
    next.setDate(1);
    next.setHours(0, 0, 0, 0);
    return next;
  }
  next.setMonth(0, 1);
  next.setHours(0, 0, 0, 0);
  return next;
}

function formatBucketLabel(date: Date, bucket: GridboxBucket): string {
  if (bucket === 'hour') {
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).replace(/:\d{2}$/, ':00');
  }
  if (bucket === 'day') {
    return date.toLocaleDateString('tr-TR');
  }
  if (bucket === 'month') {
    return date.toLocaleDateString('tr-TR', { month: '2-digit', year: 'numeric' });
  }
  return date.toLocaleDateString('tr-TR', { year: 'numeric' });
}

function formatDrillLabel(drill: DrillSelection): string {
  if (drill.bucket === 'year') return 'Yıllık';
  if (drill.bucket === 'month') return `${drill.year ?? '—'} / Aylık`;
  if (drill.bucket === 'day') return `${drill.year ?? '—'} / ${drill.month ? pad2(drill.month) : '—'} / Günlük`;
  return `${drill.year ?? '—'} / ${drill.month ? pad2(drill.month) : '—'} / ${drill.day ? pad2(drill.day) : '—'} / Saatlik`;
}

function getArchiveRangeForDrill(drill: DrillSelection): { startDate: string; endDate: string } {
  const today = new Date();
  const todayIso = formatDateUTC(today);

  if (drill.bucket === 'year') {
    if (typeof drill.year === 'number') {
      return {
        startDate: `${drill.year}-01-01`,
        endDate: `${drill.year}-12-31`,
      };
    }

    const start = new Date(Date.UTC(today.getUTCFullYear() - 4, 0, 1));
    return {
      startDate: formatDateUTC(start),
      endDate: todayIso,
    };
  }

  if (drill.bucket === 'month' && typeof drill.year === 'number') {
    return {
      startDate: `${drill.year}-01-01`,
      endDate: `${drill.year}-12-31`,
    };
  }

  if (drill.bucket === 'day' && typeof drill.year === 'number' && typeof drill.month === 'number') {
    const lastDay = new Date(Date.UTC(drill.year, drill.month, 0)).getUTCDate();
    return {
      startDate: `${drill.year}-${pad2(drill.month)}-01`,
      endDate: `${drill.year}-${pad2(drill.month)}-${pad2(lastDay)}`,
    };
  }

  if (drill.bucket === 'hour' && typeof drill.year === 'number' && typeof drill.month === 'number' && typeof drill.day === 'number') {
    const date = `${drill.year}-${pad2(drill.month)}-${pad2(drill.day)}`;
    return { startDate: date, endDate: date };
  }

  return {
    startDate: formatDateUTC(new Date(Date.UTC(today.getUTCFullYear() - 4, 0, 1))),
    endDate: todayIso,
  };
}

function buildGridboxEnergyPoints(
  rows: GridboxArchiveRow[],
  bucket: GridboxBucket,
  configuredMultiplier?: number | null,
): Omit<EnergySeriesPoint, 'production' | 'cumulativeProduction'>[] {
  const sorted = rows
    .map((row) => {
      const date = parseGridboxDate(row.ProfileDate);
      if (!date) return null;
      return { date, row: row as unknown as GridboxRecord };
    })
    .filter((item): item is { date: Date; row: GridboxRecord } => Boolean(item))
    .sort((a, b) => a.date.getTime() - b.date.getTime() || Number(a.row.id ?? 0) - Number(b.row.id ?? 0));
  const meterKey = (row: GridboxRecord) => JSON.stringify([
    row.siteId ?? row.site_id ?? null, row.WiringNo ?? null, row.MeterSerialNumber ?? null,
  ]);
  const meterReadings = new Map<string, typeof sorted>();
  for (const entry of sorted) {
    const identity = meterKey(entry.row);
    const readings = meterReadings.get(identity) ?? [];
    readings.push(entry);
    meterReadings.set(identity, readings);
  }
  const multipliers = new Map(Array.from(meterReadings, ([identity, readings]) => [
    identity,
    configuredMultiplier && configuredMultiplier > 0 ? configuredMultiplier : inferGridboxDiffMultiplier(readings),
  ]));

  const grouped = new Map<string, EnergySeriesPoint>();
  for (const entry of sorted) {
    const start = bucketStart(entry.date, bucket);
    const key = start.toISOString();
    const identity = meterKey(entry.row);
    const groupingKey = JSON.stringify([key, identity]);
    const existing = grouped.get(groupingKey);
    const diffMultiplier = multipliers.get(identity) ?? 1;
    const consumptionValue = getNullableGridboxPeriodValue(entry.row, [
      'ActiveEndexDifference',
      'ActiveT0IndexDifference',
    ]);
    const consumption = consumptionValue === null ? null : consumptionValue * diffMultiplier;
    const outgoingValue = getNullableGridboxPeriodValue(entry.row, [
      'ActiveEndexOutDiff',
    ]);
    const outgoing = outgoingValue === null ? null : outgoingValue * diffMultiplier;
    const hasOsosData = toNumber(getAnyField(entry.row, ['ActiveEndexOutDiff'])) !== null;
    const cumulativeConsumption = getNullableGridboxPeriodValue(entry.row, [
      'ActiveEndexWithMultiplier',
    ]);
    const cumulativeOutgoing = getNullableGridboxPeriodValue(entry.row, [
      'ActiveEndexOutWithMultiplier',
    ]);
    if (!existing) {
      grouped.set(groupingKey, {
        key,
        label: formatBucketLabel(start, bucket),
        startAt: key,
        production: 0,
        cumulativeProduction: 0,
        consumption,
        cumulativeConsumption,
        outgoing,
        cumulativeOutgoing,
        hasOsosData,
        selfConsumption: 0,
        cumulativeSelfConsumption: 0,
        net: outgoing !== null && consumption !== null ? outgoing - consumption : null,
      });
      continue;
    }

    existing.consumption = sumAvailable(existing.consumption, consumption);
    existing.outgoing = sumAvailable(existing.outgoing, outgoing);
    existing.hasOsosData ||= hasOsosData;
    existing.cumulativeConsumption = cumulativeConsumption ?? existing.cumulativeConsumption;
    existing.cumulativeOutgoing = cumulativeOutgoing ?? existing.cumulativeOutgoing;
    existing.net = existing.outgoing !== null && existing.consumption !== null ? existing.outgoing - existing.consumption : null;
  }

  const periods = new Map<string, EnergySeriesPoint>();
  for (const meter of grouped.values()) {
    const existing = periods.get(meter.key);
    if (!existing) {
      periods.set(meter.key, { ...meter });
      continue;
    }
    existing.consumption = sumAvailable(existing.consumption, meter.consumption);
    existing.outgoing = sumAvailable(existing.outgoing, meter.outgoing);
    existing.cumulativeConsumption = sumAvailable(existing.cumulativeConsumption, meter.cumulativeConsumption);
    existing.cumulativeOutgoing = sumAvailable(existing.cumulativeOutgoing, meter.cumulativeOutgoing);
    existing.hasOsosData ||= meter.hasOsosData;
    existing.net = existing.outgoing !== null && existing.consumption !== null ? existing.outgoing - existing.consumption : null;
  }
  const ordered = Array.from(periods.values()).sort((a, b) => a.key.localeCompare(b.key));
  return ordered.map((item) => {
    return {
      ...item,
      selfConsumption: item.outgoing !== null ? Math.max(0, item.production - item.outgoing) : null,
      cumulativeSelfConsumption: item.cumulativeOutgoing !== null ? Math.max(0, item.cumulativeProduction - item.cumulativeOutgoing) : null,
    };
  });
}

function mergeEnergySeries(
  productionRows: SiteProductionSeriesRow[],
  gridboxRows: GridboxArchiveRow[],
  bucket: GridboxBucket,
  configuredMultiplier?: number | null,
): EnergySeriesPoint[] {
  const productionPoints = buildProductionSeriesPoints(productionRows).map((row) => {
    const date = parseGridboxDate(row.startAt);
    const key = (date ? bucketStart(date, bucket) : new Date(row.startAt)).toISOString();
    return { ...row, key };
  });

  const gridboxPoints = buildGridboxEnergyPoints(gridboxRows, bucket, configuredMultiplier);
  const combined = new Map<string, EnergySeriesPoint>();

  for (const row of gridboxPoints) {
    combined.set(row.key, {
      ...row,
      production: 0,
      cumulativeProduction: 0,
    });
  }

  for (const row of productionPoints) {
    const existing = combined.get(row.key);
    if (!existing) {
      combined.set(row.key, {
        ...row,
        consumption: null,
        cumulativeConsumption: null,
        outgoing: null,
        cumulativeOutgoing: null,
        net: null,
      });
      continue;
    }
    existing.label = row.label || existing.label;
    existing.startAt = row.startAt || existing.startAt;
    existing.production = row.production;
    existing.cumulativeProduction = row.cumulativeProduction;
    existing.selfConsumption = existing.outgoing !== null ? Math.max(0, row.production - existing.outgoing) : null;
    existing.net = existing.outgoing !== null && existing.consumption !== null ? existing.outgoing - existing.consumption : null;
  }

  const ordered = Array.from(combined.values())
    .sort((a, b) => a.key.localeCompare(b.key));

  return ordered.map((row) => {
    const selfConsumption = row.outgoing !== null ? Math.max(0, row.production - row.outgoing) : null;
    return {
      ...row,
      selfConsumption,
      cumulativeSelfConsumption: row.cumulativeOutgoing !== null ? Math.max(0, row.cumulativeProduction - row.cumulativeOutgoing) : null,
      net: row.outgoing !== null && row.consumption !== null ? row.outgoing - row.consumption : null,
    };
  });
}

function SectionTitle({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
        {icon}
      </div>
      <div>
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
        {description && <p className="text-sm text-slate-500">{description}</p>}
      </div>
    </div>
  );
}

function HighchartsColumnChart({
  options,
  height,
}: {
  options: Highcharts.Options;
  height: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const chart = Highcharts.chart(containerRef.current, options);
    return () => chart.destroy();
  }, [options]);

  return (
    <div
      ref={containerRef}
      style={{ height: `${height}px` }}
      className="w-full"
    />
  );
}

function EkkMetricIcon({ tag }: { tag: string | null }) {
  if (tag?.includes('voltage')) return <Gauge size={15} />;
  if (tag?.includes('current')) return <Activity size={15} />;
  if (tag?.includes('frequency')) return <Radio size={15} />;
  if (tag?.includes('temperature')) return <Thermometer size={15} />;
  return <Zap size={15} />;
}

function localDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function shiftDate(date: string, days: number): string {
  const next = new Date(`${date}T12:00:00`);
  next.setDate(next.getDate() + days);
  return localDateString(next);
}

function EkkSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className="animate-pulse rounded-2xl border border-slate-100 bg-slate-50 p-4">
          <div className="h-3 w-24 rounded bg-slate-200" />
          <div className="mt-4 h-6 w-32 rounded bg-slate-200" />
          <div className="mt-3 h-3 w-20 rounded bg-slate-200" />
        </div>
      ))}
    </div>
  );
}

function EkkThumbnailChart({ rows }: { rows: SiteEkkHistoryRow[] }) {
  const values = rows.map((row) => Number(row.valueNumeric)).filter((value) => Number.isFinite(value));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((value, index) => {
    const x = values.length === 1 ? 120 : (index / (values.length - 1)) * 232 + 4;
    const y = 68 - ((value - min) / range) * 60;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  return (
    <svg viewBox="0 0 240 76" preserveAspectRatio="none" className="h-[76px] w-full" aria-hidden="true">
      <polyline points={points} fill="none" stroke="#2563eb" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
  );
}

function EkkHistoryChart({ rows, unit, height = 300 }: { rows: SiteEkkHistoryRow[]; unit: string | null; height?: number }) {
  const options: Highcharts.Options = {
    chart: { type: 'line', height, backgroundColor: 'transparent' },
    title: { text: '' },
    credits: { enabled: false },
    xAxis: { type: 'datetime', title: { text: 'Saat' } },
    yAxis: { title: { text: unit || 'Değer' } },
    tooltip: { valueDecimals: 2, valueSuffix: unit ? ` ${unit}` : '' },
    legend: { enabled: false },
    series: [{
      type: 'line',
      name: 'Değer',
      color: '#2563eb',
      data: rows.map((row) => [new Date(row.timestamp).getTime(), Number(row.valueNumeric)]),
    }],
  };
  return <HighchartsColumnChart options={options} height={height} />;
}

function EkkPanel({ site, collapsible = true }: { site: Site; collapsible?: boolean }) {
  const [open, setOpen] = useState(!collapsible);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);
  const [rows, setRows] = useState<SiteEkkCurrentRow[]>([]);
  const [selected, setSelected] = useState<SiteEkkCurrentRow | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyDate, setHistoryDate] = useState(localDateString(new Date()));
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  useErrorToast(historyError);
  const [historyRows, setHistoryRows] = useState<SiteEkkHistoryRow[]>([]);
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  const [thumbnailRows, setThumbnailRows] = useState<SiteEkkHistoryRow[]>([]);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const currentCache = useRef(new Map<string, SiteEkkCurrentRow[]>());
  const historyCache = useRef(new Map<string, SiteEkkHistoryRow[]>());

  useEffect(() => {
    setOpen(!collapsible);
    setRows(currentCache.current.get(site.uuid_id) || []);
    setSelected(null);
    setHistoryOpen(false);
    setHistoryRows([]);
    setHoveredRowId(null);
    setThumbnailRows([]);
    setHistoryError(null);
    setHistoryDate(localDateString(new Date()));
  }, [site.uuid_id, collapsible]);

  useEffect(() => {
    if (!open || !site.is_active || currentCache.current.has(site.uuid_id)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchSiteEkkCurrent(site.uuid_id)
      .then((response) => {
        if (cancelled) return;
        currentCache.current.set(site.uuid_id, response.rows || []);
        setRows(response.rows || []);
      })
      .catch((reason) => {
        if (!cancelled) {
          setError(`EKK verileri yüklenemedi: ${extractErrorMessage(reason)}`);
          console.error('[Sites] EKK current load failed:', reason);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, site.is_active, site.uuid_id]);

  useEffect(() => {
    if (!selected || !site.is_active) return;
    const cacheKey = `${site.uuid_id}:${selected.id}:${historyDate}`;
    const cached = historyCache.current.get(cacheKey);
    if (cached) {
      setHistoryRows(cached);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    void fetchSiteEkkHistory(site.uuid_id, { registerMapId: selected.id, date: historyDate })
      .then((response) => {
        if (cancelled) return;
        historyCache.current.set(cacheKey, response.rows || []);
        setHistoryRows(response.rows || []);
      })
      .catch((reason) => {
        if (!cancelled) {
          setHistoryRows([]);
          setHistoryError(`Geçmiş verisi yüklenemedi: ${extractErrorMessage(reason)}`);
          console.error('[Sites] EKK history load failed:', reason);
        }
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [historyDate, selected, site.is_active, site.uuid_id]);

  useEffect(() => {
    if (hoveredRowId === null || !site.is_active) {
      setThumbnailLoading(false);
      return;
    }
    const cacheKey = `${site.uuid_id}:${hoveredRowId}:${localDateString(new Date())}`;
    const cached = historyCache.current.get(cacheKey);
    if (cached) {
      setThumbnailLoading(false);
      setThumbnailRows(cached);
      return;
    }
    const row = rows.find((item) => item.id === hoveredRowId);
    if (!row) return;
    let cancelled = false;
    setThumbnailRows([]);
    setThumbnailLoading(true);
    void fetchSiteEkkHistory(site.uuid_id, {
      registerMapId: row.id,
      date: localDateString(new Date()),
    })
      .then((response) => {
        if (cancelled) return;
        historyCache.current.set(cacheKey, response.rows || []);
        setThumbnailRows(response.rows || []);
      })
      .catch((reason) => {
        if (!cancelled) console.error('[Sites] EKK thumbnail load failed:', reason);
      })
      .finally(() => {
        if (!cancelled) setThumbnailLoading(false);
      });
    return () => { cancelled = true; };
  }, [hoveredRowId, rows, site.is_active, site.uuid_id]);

  const toggle = () => setOpen((previous) => !previous);
  const tagClass = (tag: string | null) => tag?.includes('power')
    ? 'bg-amber-50 text-amber-700'
    : tag?.includes('voltage')
      ? 'bg-blue-50 text-blue-700'
      : 'bg-slate-100 text-slate-600';
  const devices = Array.from(new Map(rows.map((row) => [row.deviceId, row])).values());

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      {collapsible ? <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-700">EKK Elektriksel Veriler</div>
          <div className="mt-1 text-sm text-slate-500">Sahanın EKK cihazlarından alınan son okuma değerleri</div>
        </div>
        {open ? <ChevronUp size={19} className="text-slate-400" /> : <ChevronDown size={19} className="text-slate-400" />}
      </button> : <h3 className="px-5 py-3 text-sm font-semibold text-slate-700">EKK Elektriksel Veriler</h3>}

      {open && (
        <div className="border-t border-slate-100 px-5 pb-5 pt-4">
          {loading && <EkkSkeleton />}
          {!loading && !error && rows.length === 0 && <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-5 text-sm text-slate-500">Bu sahada EKK okuması bulunamadı.</div>}
          {!loading && rows.length > 0 && (
            <>
              <div className="mb-4 overflow-x-auto rounded-2xl border border-slate-100 bg-slate-50/70">
                <div className="flex min-w-[900px] items-center gap-5 border-b border-slate-200 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <div className="w-44 shrink-0 flex items-center gap-2"><Radio size={15} className="text-blue-600" />EKK Cihaz Tanımı</div>
                  <div className="w-40 shrink-0">Serial Number</div>
                  <div className="w-40 shrink-0">Name</div>
                  <div className="w-28 shrink-0">Brand</div>
                  <div className="w-28 shrink-0">Model</div>
                  <div className="min-w-64 flex-1">Web Base URL</div>
                </div>
                {devices.map((device) => (
                  <div key={device.deviceId} className="flex min-w-[900px] items-center gap-5 px-4 py-3 text-sm text-slate-800">
                    <div className="w-44 shrink-0 font-semibold text-slate-700">Cihaz #{device.deviceId}</div>
                    <div className="w-40 shrink-0 truncate font-semibold" title={device.serialNumber || undefined}>{device.serialNumber || '—'}</div>
                    <div className="w-40 shrink-0 truncate font-semibold" title={device.deviceName || undefined}>{device.deviceName || '—'}</div>
                    <div className="w-28 shrink-0 truncate">{device.brand || '—'}</div>
                    <div className="w-28 shrink-0 truncate">{device.model || '—'}</div>
                    <div className="min-w-64 flex-1 truncate">
                      {device.webBaseUrl ? (
                        <a href={device.webBaseUrl} target="_blank" rel="noreferrer" className="font-semibold text-blue-600 underline decoration-blue-200 underline-offset-2 hover:text-blue-800" title={device.webBaseUrl}>
                          {device.webBaseUrl}
                        </a>
                      ) : '—'}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {rows.map((row) => (
                  <button
                    type="button"
                    key={`${row.deviceId}-${row.id}`}
                    onClick={() => {
                      setSelected(row);
                      setHistoryDate(localDateString(new Date()));
                      setHistoryRows([]);
                      setHistoryError(null);
                      setHistoryOpen(true);
                    }}
                    onMouseEnter={() => setHoveredRowId(row.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    className={`relative min-h-[112px] rounded-2xl border p-4 pr-36 text-left transition hover:-translate-y-0.5 hover:shadow-md ${selected?.id === row.id ? 'border-blue-300 bg-blue-50/60 shadow-sm' : 'border-slate-100 bg-slate-50/70'}`}
                    title={row.notes || undefined}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2 text-xs font-semibold text-slate-600">
                        <span className="text-blue-600"><EkkMetricIcon tag={row.webPageTag} /></span>
                        <span className="truncate">{row.quantity}</span>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${tagClass(row.webPageTag)}`}>{row.webPageTag || 'EKK'}</span>
                    </div>
                    <div className="mt-3 text-xl font-bold tabular-nums text-slate-900">
                      {row.valueNumeric != null ? Number(row.valueNumeric).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : row.valueText || '—'}
                      {row.valueNumeric != null && row.units ? <span className="ml-1 text-xs font-semibold text-slate-500">{row.units}</span> : null}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-slate-500">
                      <span className="truncate">{row.dataType || 'Veri'}{row.notes ? ` · ${row.notes}` : ''}</span>
                    </div>
                    {hoveredRowId === row.id && (
                      <div className="absolute right-3 top-1/2 h-[76px] w-28 -translate-y-1/2 overflow-hidden rounded-xl border border-blue-100 bg-white">
                        {thumbnailLoading && <div className="h-[76px] animate-pulse bg-slate-100" />}
                        {!thumbnailLoading && thumbnailRows.length > 0 && <EkkThumbnailChart rows={thumbnailRows} />}
                        {!thumbnailLoading && thumbnailRows.length === 0 && <div className="flex h-[76px] items-center justify-center text-[10px] text-slate-400">Bugün veri yok</div>}
                      </div>
                    )}
                  </button>
                ))}
              </div>

            </>
          )}
        </div>
      )}

      {historyOpen && selected && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={() => setHistoryOpen(false)}>
          <div className="w-full max-w-4xl rounded-3xl bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-blue-700">EKK Geçmiş Grafiği</div>
                <div className="text-lg font-bold text-slate-900">{selected.quantity} · {selected.units || 'birimsiz'}</div>
                <div className="text-xs text-slate-500">{selected.deviceName}</div>
              </div>
              <button type="button" onClick={() => setHistoryOpen(false)} className="rounded-full p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-700" title="Kapat"><X size={18} /></button>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm font-semibold text-slate-700">Günlük değer grafiği</div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setHistoryDate(shiftDate(historyDate, -1))} className="rounded-lg border border-blue-200 bg-white p-2 text-blue-700 hover:bg-blue-50" title="Önceki gün"><ChevronLeft size={16} /></button>
                <input type="date" value={historyDate} onChange={(event) => setHistoryDate(event.target.value)} className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm text-slate-700" />
                <button type="button" onClick={() => setHistoryDate(shiftDate(historyDate, 1))} className="rounded-lg border border-blue-200 bg-white p-2 text-blue-700 hover:bg-blue-50" title="Sonraki gün"><ChevronRight size={16} /></button>
              </div>
            </div>
            <div className="mt-4">
              {historyLoading && <div className="flex h-[300px] items-center justify-center rounded-2xl bg-slate-50 text-sm text-slate-500"><Loader2 size={18} className="mr-2 animate-spin" />Veriler yükleniyor...</div>}
              {!historyLoading && !historyError && historyRows.length === 0 && <div className="flex h-[300px] items-center justify-center rounded-2xl bg-slate-50 text-sm text-slate-500">Seçilen gün için kayıt bulunamadı.</div>}
              {!historyLoading && !historyError && historyRows.length > 0 && <EkkHistoryChart rows={historyRows} unit={selected.units} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SitesPage() {
  const {uuid_id}=useParams<{uuid_id:string}>();
  const navigate=useNavigate();
  const headerActionsTarget = useHeaderActionsTarget();
  const [coverageOpen, setCoverageOpen] = useState(false);
  const handleDeleteSite=async(site:Site)=>{
    if(!(await confirmToast(`${site.name} sahası silinsin mi?`,'Bağlı santraller silinmez, saha ilişkileri kaldırılır.')))return;
    try{
      await deleteSite(site.uuid_id);
      toast.success('Saha silindi');
      navigate('/dashboard/sites');
    }catch(error){
      toast.error(`Saha silinemedi: ${extractErrorMessage(error)}`);
    }
  };
  const [detailTab, setDetailTab] = useState('osos');
  useEffect(() => { setDetailTab('osos'); }, [uuid_id]);
  const [searchParams,setSearchParams]=useSearchParams();
  const loadSitesRequest=useRef(0);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  usePageTitle(uuid_id && selectedSite?.uuid_id === uuid_id ? selectedSite.name : null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<'general' | 'connection' | 'plants' | 'ekk'>('general');
  const [ekkConfigRevision,setEkkConfigRevision]=useState(0);
  const [sitePlants, setSitePlants] = useState<SitePlant[]>([]);
  const [availablePlants, setAvailablePlants] = useState<PlantForSelection[]>([]);
  const [plantsLoading, setPlantsLoading] = useState(false);
  const [plantToAdd, setPlantToAdd] = useState<string>('');
  const [plantOpLoading, setPlantOpLoading] = useState(false);
  const [productionSeriesRows, setProductionSeriesRows] = useState<SiteProductionSeriesRow[]>([]);
  const [gridboxArchiveRows, setGridboxArchiveRows] = useState<GridboxArchiveRow[]>([]);
  const [gridboxEndexRows, setGridboxEndexRows] = useState<GridboxEndexRow[]>([]);
  const [gridboxBillingEndexRows, setGridboxBillingEndexRows] = useState<GridboxEndexRow[]>([]);
  const [productionSeriesLoading, setProductionSeriesLoading] = useState(false);
  const [gridboxArchiveLoading, setGridboxArchiveLoading] = useState(false);
  const [gridboxRefreshLoading, setGridboxRefreshLoading] = useState(false);
  const [gridboxTestLoading, setGridboxTestLoading] = useState(false);
  const [gridboxTestResult, setGridboxTestResult] = useState<{
    ok: boolean;
    message: string;
    checks: {
      token: { ok: boolean; message: string };
      endex: { ok: boolean; message: string };
      billingEndex: { ok: boolean; message: string };
      loadProfile: { ok: boolean; message: string };
    };
  } | null>(null);
  const [drillSelection, setDrillSelection] = useState<DrillSelection>({ bucket: 'year' });
  const [productionSeriesError, setProductionSeriesError] = useState<string | null>(null);
  useErrorToast(productionSeriesError);
  const [gridboxArchiveError, setGridboxArchiveError] = useState<string | null>(null);
  useErrorToast(gridboxArchiveError);
  const [endexPageSize] = useState(100);
  const selectedSiteRef = useRef<string | null>(null);
  const gridRef = useRef<AgGridReact<EnergySeriesPoint>>(null);

  const [formData, setFormData] = useState<Partial<Site>>({
    name: '',
    description: '',
    company_name: '',
    is_active: true,
    api_config: {
      gridbox: {
        wiringNo: '',
      },
    },
  });

  useEffect(() => {
    loadSites();
    return()=>{loadSitesRequest.current++;};
  }, [uuid_id]);

  useEffect(()=>{
    if(searchParams.get('edit')!=='1'||!selectedSite||selectedSite.uuid_id!==uuid_id)return;
    setFormData(selectedSite);setModalTab('general');setSitePlants([]);setAvailablePlants([]);setIsModalOpen(true);
    const next=new URLSearchParams(searchParams);next.delete('edit');setSearchParams(next,{replace:true});
  },[selectedSite,uuid_id,searchParams,setSearchParams]);

  const closeSiteModal=()=>{setIsModalOpen(false);if(uuid_id==='new')navigate('/dashboard/sites');};

  const loadSites = async () => {
    const requestId=++loadSitesRequest.current;
    if(uuid_id&&uuid_id!==selectedSite?.uuid_id){selectedSiteRef.current=null;setSelectedSite(null);}
    setLoading(true);
    try {
      const data = await fetchSites();
      if(requestId!==loadSitesRequest.current)return;
      if(uuid_id==='new'){
        setSelectedSite(null);setFormData({name:'',description:'',company_name:'',is_active:true,api_config:{gridbox:{wiringNo:''}}});
        setModalTab('general');setIsModalOpen(true);
      }else{
        const target=uuid_id?data.find(site=>site.uuid_id===uuid_id):data[0];
        if(target){if(selectedSite?.uuid_id!==target.uuid_id)await handleSiteClick(target);else setSelectedSite(target);}
        else setSelectedSite(null);
      }
    } catch (err) {
      toast.error('Sahalar yüklenemedi');
    } finally {
      if(requestId===loadSitesRequest.current)setLoading(false);
    }
  };

  const loadSiteEnergyData = async (
    site: Site,
    drill: DrillSelection,
  ) => {
    if (!site.is_active) {
      setProductionSeriesRows([]);
      setGridboxArchiveRows([]);
      setGridboxEndexRows([]);
      setGridboxBillingEndexRows([]);
      setProductionSeriesError(null);
      setGridboxArchiveError(null);
      setProductionSeriesLoading(false);
      setGridboxArchiveLoading(false);
      return;
    }
    const canLoadGridbox = Boolean(site.api_config?.gridbox?.wiringNo);
    setProductionSeriesLoading(true);
    setGridboxArchiveLoading(true);
    setProductionSeriesError(null);
    setGridboxArchiveError(null);

    const productionPromise = fetchSiteProductionSeries(site.uuid_id, {
      bucket: drill.bucket,
      year: drill.year,
      month: drill.month,
      day: drill.day,
    });
    const archiveRange = getArchiveRangeForDrill(drill);

    const gridboxPromise = canLoadGridbox
      ? fetchSiteGridboxArchive(site.uuid_id, {
        limit: 50000,
        startDate: archiveRange.startDate,
        endDate: archiveRange.endDate,
      })
      : Promise.resolve(null);
    const endexPromise = canLoadGridbox
      ? fetchSiteGridboxEndexArchive(site.uuid_id, {
        limit: 50000,
        startDate: archiveRange.startDate,
        endDate: archiveRange.endDate,
        isBilling: false,
      })
      : Promise.resolve(null);
    const billingEndexPromise = canLoadGridbox
      ? fetchSiteGridboxEndexArchive(site.uuid_id, {
        limit: 50000,
        startDate: archiveRange.startDate,
        endDate: archiveRange.endDate,
        isBilling: true,
      })
      : Promise.resolve(null);

    // The load-profile response can be very large. Let the page render the production
    // and endex tables first while this archive continues in the background.
    const [productionResult, endexResult, billingEndexResult] = await Promise.allSettled([
      productionPromise,
      endexPromise,
      billingEndexPromise,
    ]);
    if (selectedSiteRef.current !== site.uuid_id) {
      setProductionSeriesLoading(false);
      setGridboxArchiveLoading(false);
      return;
    }

    if (productionResult.status === 'fulfilled') {
      setProductionSeriesRows(productionResult.value.rows || []);
    } else {
      setProductionSeriesRows([]);
      setProductionSeriesError('Santral üretim serisi yüklenemedi.');
      console.error('[Sites] Production series load failed:', productionResult.reason);
    }

    if (!canLoadGridbox) {
      setGridboxArchiveRows([]);
      setGridboxEndexRows([]);
      setGridboxBillingEndexRows([]);
      setGridboxArchiveError(null);
    } else {
      setGridboxEndexRows(endexResult.status === 'fulfilled' && endexResult.value ? endexResult.value.rows || [] : []);
      setGridboxBillingEndexRows(billingEndexResult.status === 'fulfilled' && billingEndexResult.value ? billingEndexResult.value.rows || [] : []);
    }

    setProductionSeriesLoading(false);

    if (!canLoadGridbox || selectedSiteRef.current !== site.uuid_id) {
      setGridboxArchiveLoading(false);
      return;
    }

    const [gridboxResult] = await Promise.allSettled([gridboxPromise]);
    if (selectedSiteRef.current !== site.uuid_id) {
      setGridboxArchiveLoading(false);
      return;
    }
    if (gridboxResult.status === 'fulfilled' && gridboxResult.value) {
      setGridboxArchiveRows(gridboxResult.value.rows || []);
    } else {
      setGridboxArchiveRows([]);
      setGridboxArchiveError('Enerji şirketi verileri yüklenemedi.');
      if (gridboxResult.status === 'rejected') {
        console.error('[Sites] GridBox archive load failed:', gridboxResult.reason);
      }
    }
    setGridboxArchiveLoading(false);
  };

  const handleSiteClick = async (site: Site) => {
    if(uuid_id&&uuid_id!==site.uuid_id){navigate(`/dashboard/sites/${site.uuid_id}`);return;}
    selectedSiteRef.current = site.uuid_id;
    setSelectedSite(site);
    setProductionSeriesRows([]);
    setGridboxArchiveRows([]);
    setGridboxEndexRows([]);
    setGridboxBillingEndexRows([]);
    setDrillSelection({ bucket: 'year' });
    setProductionSeriesError(null);
    setGridboxArchiveError(null);
    if (!site.is_active) return;
    await loadSiteEnergyData(site, { bucket: 'year' });
  };

  const handleDrillClick = async (nextDrill: DrillSelection) => {
    setDrillSelection(nextDrill);
    if (selectedSite && selectedSite.is_active) {
      await loadSiteEnergyData(selectedSite, nextDrill);
    }
  };

  const handleDrillBack = async () => {
    if (!selectedSite || !selectedSite.is_active) return;
    const nextDrill: DrillSelection = drillSelection.bucket === 'hour'
      ? { bucket: 'day', year: drillSelection.year, month: drillSelection.month }
      : drillSelection.bucket === 'day'
        ? { bucket: 'month', year: drillSelection.year }
        : drillSelection.bucket === 'month'
          ? { bucket: 'year' }
          : drillSelection;

    if (nextDrill.bucket === drillSelection.bucket) return;
    setDrillSelection(nextDrill);
    await loadSiteEnergyData(selectedSite, nextDrill);
  };

  const handleGridboxTest = async () => {
    if (!formData.uuid_id) {
      toast.error('Önce sahayı kaydedin.');
      return;
    }
    setGridboxTestLoading(true);
    setGridboxTestResult(null);
    try {
      const result = await testSiteGridboxConnection(formData.uuid_id);
      const tokenExpiresIn = result?.result?.tokenExpiresIn;
      const checks = result?.result?.checks;
      const message = result.ok
        ? (tokenExpiresIn ? `Tüm kontroller başarılı (token ${tokenExpiresIn}s).` : 'Tüm kontroller başarılı.')
        : 'Bazı kontroller başarısız.';
      if (checks) {
        setGridboxTestResult({ ok: result.ok, message, checks });
      }
      if (result.ok) toast.success(message);
      else toast.error(message);
    } catch (error) {
      const message = extractErrorMessage(error);
      setGridboxTestResult(null);
      toast.error(`Bağlantı başarısız: ${message}`);
    } finally {
      setGridboxTestLoading(false);
    }
  };

  const handleGridboxRefresh = async () => {
    if (!selectedSite || !selectedSite.is_active) return;

    setGridboxRefreshLoading(true);
    try {
      await loadSiteEnergyData(selectedSite, drillSelection);
      toast.success('Veriler yenilendi.');
    } catch (error) {
      console.error('[Sites] Refresh failed:', error);
      toast.error(`Veriler yenilenemedi: ${extractErrorMessage(error)}`);
    } finally {
      setGridboxRefreshLoading(false);
    }
  };

  useEffect(() => {
    if (isModalOpen && modalTab === 'plants' && formData.uuid_id) {
      void loadPlantsForModal(formData.uuid_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, modalTab, formData.uuid_id]);

  const loadPlantsForModal = async (uuidId: string) => {
    setPlantsLoading(true);
    try {
      const [assigned, available] = await Promise.all([
        fetchSitePlants(uuidId),
        fetchAllPlantsForSelection(),
      ]);
      setSitePlants(assigned);
      setAvailablePlants(available);
    } catch (err) {
      toast.error(`Santraller yüklenemedi: ${extractErrorMessage(err)}`);
    } finally {
      setPlantsLoading(false);
    }
  };

  const handleAddPlantToSite = async () => {
    if (!formData.uuid_id || !plantToAdd) return;
    setPlantOpLoading(true);
    try {
      await assignPlantsToSite(formData.uuid_id, [plantToAdd]);
      await loadPlantsForModal(formData.uuid_id);
      await loadSites();
      setPlantToAdd('');
      toast.success('Santral eklendi');
    } catch (err) {
      toast.error(`Eklenemedi: ${extractErrorMessage(err)}`);
    } finally {
      setPlantOpLoading(false);
    }
  };

  const handleRemovePlantFromSite = async (plantUuid: string) => {
    if (!formData.uuid_id) return;
    setPlantOpLoading(true);
    try {
      await removePlantFromSite(plantUuid);
      await loadPlantsForModal(formData.uuid_id);
      await loadSites();
      toast.success('Santral çıkarıldı');
    } catch (err) {
      toast.error(`Çıkarılamadı: ${extractErrorMessage(err)}`);
    } finally {
      setPlantOpLoading(false);
    }
  };

  const handleCreateSite = async (e: React.FormEvent) => {
    e.preventDefault();
    if(modalTab==='ekk')return;
    try {
      const dataToSave = {
        ...formData,
        api_config: formData.api_config || { gridbox: { wiringNo: '' } },
      };

      if (formData.uuid_id) {
        await updateSite(formData.uuid_id, dataToSave);
        toast.success('Saha güncellendi');
      } else {
        const created=await createSite(dataToSave);
        toast.success('Saha oluşturuldu');
        setIsModalOpen(false);navigate(`/dashboard/sites/${created.uuid_id}`);return;
      }
      setIsModalOpen(false);
      await loadSites();
    } catch {
      toast.error('Hata oluştu');
    }
  };

  useEffect(() => {
    const saveInferredMultiplier = async () => {
      if (!selectedSite || !selectedSite.is_active) return;
      if ((selectedSite.gridbox_diff_multiplier ?? 0) > 0) return;
      if (gridboxArchiveRows.length < 2) return;

      const inferred = inferGridboxDiffMultiplierFromArchiveRows(gridboxArchiveRows);
      if (!Number.isFinite(inferred) || inferred <= 1) return;

      try {
        await updateSite(selectedSite.uuid_id, {
          ...selectedSite,
          gridbox_diff_multiplier: inferred,
        });
        setSelectedSite((prev) => (prev && prev.uuid_id === selectedSite.uuid_id
          ? { ...prev, gridbox_diff_multiplier: inferred }
          : prev));
      } catch (error) {
        console.error('[Sites] Failed to persist gridbox multiplier:', error);
      }
    };

    void saveInferredMultiplier();
  }, [selectedSite, gridboxArchiveRows]);

  const combinedEnergyPoints = useMemo(() => mergeEnergySeries(
    productionSeriesRows,
    gridboxArchiveRows,
    drillSelection.bucket,
    selectedSite?.gridbox_diff_multiplier,
  ), [productionSeriesRows, gridboxArchiveRows, drillSelection.bucket, selectedSite?.gridbox_diff_multiplier]);
  const endexColDefs = useMemo<ColDef<GridboxEndexRow>[]>(() => [
    {
      headerName: 'Endex Tarihi',
      field: 'endexDate',
      minWidth: 180,
      valueFormatter: ({ value }) => value ? new Date(String(value)).toLocaleString('tr-TR') : '—',
      sort: 'desc',
    },
    { headerName: 'TOutToplam', field: 'TOutToplam', minWidth: 130, type: 'numericColumn' },
    { headerName: 'TToplam', field: 'TToplam', minWidth: 130, type: 'numericColumn' },
    { headerName: 'RCOut', field: 'RCOut', minWidth: 110, type: 'numericColumn' },
    { headerName: 'RC', field: 'RC', minWidth: 110, type: 'numericColumn' },
    { headerName: 'RIOut', field: 'RIOut', minWidth: 110, type: 'numericColumn' },
    { headerName: 'RI', field: 'RI', minWidth: 110, type: 'numericColumn' },
    { headerName: 'MaxDemand', field: 'MaxDemand', minWidth: 120, type: 'numericColumn' },
    {
      headerName: 'MaxDemandDate',
      field: 'MaxDemandDate',
      minWidth: 170,
      valueFormatter: ({ value }) => value ? new Date(String(value)).toLocaleString('tr-TR') : '—',
    },
  ], []);
  const seriesStartLabel = combinedEnergyPoints[0]?.label ?? null;
  const seriesEndLabel = combinedEnergyPoints[combinedEnergyPoints.length - 1]?.label ?? null;
  const tableUnit = 'kWh' as const;
  const chartUnit = 'kWh' as const;
  const combinedEnergyColDefs = useMemo(() => [
    {
      headerName: 'Dönem',
      field: 'label',
      flex: 1.2,
      minWidth: 180,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellStyle: {
        cursor: drillSelection.bucket === 'hour' ? 'default' : 'pointer',
        color: drillSelection.bucket === 'hour' ? '#0f172a' : '#1d4ed8',
        fontWeight: 700,
      },
    },
    {
      headerName: 'GES Üretimi',
      children: [
        {
          headerName: 'Üretim',
          field: 'production',
          flex: 1,
          minWidth: 140,
          type: 'numericColumn',
          valueFormatter: ({ value }: { value: unknown }) => formatEnergyValue(value, tableUnit),
          cellClass: 'ag-right-aligned-cell tabular-nums',
          headerClass: 'ag-right-aligned-header',
          cellStyle: { justifyContent: 'flex-end' },
          enableValue: true,
          aggFunc: 'sum',
        },
        {
          headerName: 'Kümülatif Üretim',
          field: 'cumulativeProduction',
          flex: 1.15,
          minWidth: 170,
          type: 'numericColumn',
          valueFormatter: ({ value }: { value: unknown }) => formatEnergyValue(value, tableUnit),
          cellClass: 'ag-right-aligned-cell tabular-nums',
          headerClass: 'ag-right-aligned-header',
          cellStyle: { justifyContent: 'flex-end' },
          enableValue: true,
          aggFunc: 'max',
        },
        {
          headerName: 'Öz Tüketim',
          field: 'selfConsumption',
          flex: 1,
          minWidth: 160,
          type: 'numericColumn',
          valueFormatter: ({ value }: { value: unknown }) => value == null ? 'Veri Yok' : formatEnergyValue(value, tableUnit),
          cellClass: 'ag-right-aligned-cell tabular-nums',
          headerClass: 'ag-right-aligned-header',
          cellStyle: { justifyContent: 'flex-end' },
          enableValue: true,
          aggFunc: 'sum',
        },
      ],
    },
    {
      headerName: 'Şebeke / OSOS',
      children: [
        {
          headerName: 'Şebekeye Verilen',
          field: 'outgoing',
          flex: 1,
          minWidth: 150,
          type: 'numericColumn',
          valueFormatter: ({ value }: { value: unknown }) => value == null ? 'Veri Yok' : formatEnergyValue(value, tableUnit),
          cellClass: 'ag-right-aligned-cell tabular-nums',
          headerClass: 'ag-right-aligned-header',
          cellStyle: { justifyContent: 'flex-end' },
          enableValue: true,
          aggFunc: 'sum',
        },
        {
          headerName: 'Veriş Sayaç Endeksi',
          field: 'cumulativeOutgoing',
          flex: 1.15,
          minWidth: 180,
          type: 'numericColumn',
          valueFormatter: ({ value }: { value: unknown }) => value == null ? 'Veri Yok' : formatEnergyValue(value, tableUnit),
          cellClass: 'ag-right-aligned-cell tabular-nums',
          headerClass: 'ag-right-aligned-header',
          cellStyle: { justifyContent: 'flex-end' },
          enableValue: true,
          aggFunc: 'max',
        },
        {
          headerName: 'Şebekeden Alınan',
          field: 'consumption',
          flex: 1.15,
          minWidth: 190,
          type: 'numericColumn',
          valueFormatter: ({ value }: { value: unknown }) => value == null ? 'Veri Yok' : formatEnergyValue(value, tableUnit),
          cellClass: 'ag-right-aligned-cell tabular-nums',
          headerClass: 'ag-right-aligned-header',
          cellStyle: { justifyContent: 'flex-end' },
          enableValue: true,
          aggFunc: 'sum',
        },
        {
          headerName: 'Net',
          field: 'net',
          flex: 1,
          minWidth: 140,
          type: 'numericColumn',
          valueFormatter: ({ value }: { value: unknown }) => value == null ? 'Veri Yok' : formatEnergyValue(value, tableUnit),
          cellClass: 'ag-right-aligned-cell tabular-nums',
          headerClass: 'ag-right-aligned-header',
          cellStyle: { justifyContent: 'flex-end' },
          enableValue: true,
          aggFunc: 'sum',
        },
      ],
    },
  ] satisfies (ColDef<EnergySeriesPoint> | ColGroupDef<EnergySeriesPoint>)[], [drillSelection.bucket, tableUnit]);

  const gridboxDefaultColDef = {
    resizable: true,
    sortable: true,
    filter: false,
    suppressHeaderMenuButton: true,
    enablePivot: true,
    enableRowGroup: true,
  } satisfies ColDef<EnergySeriesPoint>;

  const handleGridReady = (_event: GridReadyEvent<EnergySeriesPoint>) => {
    // AG Grid enterprise side panel is controlled via the toolbar buttons below.
  };

  const handleProductionPointClick = (point: EnergySeriesPoint) => {
    if (!selectedSite) return;
    if (drillSelection.bucket === 'hour') return;
    if (!point?.startAt) return;

    const pointDate = parseGridboxDate(point.startAt);
    if (!pointDate) return;

    if (drillSelection.bucket === 'year') {
      void handleDrillClick({
        bucket: 'month',
        year: pointDate.getUTCFullYear(),
      });
      return;
    }

    if (drillSelection.bucket === 'month') {
      void handleDrillClick({
        bucket: 'day',
        year: pointDate.getUTCFullYear(),
        month: pointDate.getUTCMonth() + 1,
      });
      return;
    }

    if (drillSelection.bucket === 'day') {
      void handleDrillClick({
        bucket: 'hour',
        year: pointDate.getUTCFullYear(),
        month: pointDate.getUTCMonth() + 1,
        day: pointDate.getUTCDate(),
      });
    }
  };

  const handleTableCellClick = (event: CellClickedEvent<EnergySeriesPoint>) => {
    if (drillSelection.bucket === 'hour') return;
    if (event.column.getColId() !== 'label') return;
    if (!event.data) return;
    handleProductionPointClick(event.data);
  };

  // Keep chart callbacks current without rebuilding the chart when a modal/tab changes.
  const productionPointClickRef = useRef(handleProductionPointClick);
  productionPointClickRef.current = handleProductionPointClick;
  const highchartsOptions = useMemo(() => ({
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      spacingTop: 8,
      spacingLeft: 0,
      spacingRight: 0,
      spacingBottom: 4,
      style: {
        fontFamily: 'Inter, system-ui, sans-serif',
      },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      categories: combinedEnergyPoints.map((row) => row.label),
      lineColor: '#e2e8f0',
      tickColor: '#e2e8f0',
      labels: {
        style: {
          color: '#64748b',
          fontSize: '11px',
          fontWeight: '600',
        },
      },
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: '#f8fafc',
      gridLineDashStyle: 'Dash',
      labels: {
        style: {
          color: '#94a3b8',
          fontSize: '11px',
        },
        formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
          return formatEnergyValue(this.value, chartUnit);
        },
      },
    },
    tooltip: {
      shared: false,
      useHTML: true,
      borderRadius: 16,
      borderColor: '#cbd5e1',
      backgroundColor: 'rgba(255,255,255,0.96)',
      shadow: false,
      formatter(this: any) {
        const point = this.point as Highcharts.Point & { startAt?: string };
        return `
          <div style="min-width:150px;padding:12px 14px">
            <div style="font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:#64748b;margin-bottom:6px">
              ${point.category ?? ''}
            </div>
            <div style="display:flex;align-items:center;justify-content:space-between;gap:12px">
              <span style="font-size:12px;color:#0f172a">${point.series?.name ?? 'Üretim'}</span>
              <span style="font-size:16px;font-weight:700;color:#0f172a">${formatEnergyValueWithUnit(point.y, chartUnit)}</span>
            </div>
          </div>
        `;
      },
    },
    plotOptions: {
      column: {
        borderRadius: 8,
        borderWidth: 0,
        groupPadding: 0.14,
        pointPadding: 0.08,
        cursor: drillSelection.bucket === 'hour' ? 'default' : 'pointer',
        dataLabels: {
          enabled: true,
          inside: false,
          crop: false,
          overflow: 'allow',
          y: -8,
          style: {
            color: '#0f172a',
            fontSize: '10px',
            fontWeight: '700',
            textOutline: 'none',
          },
          formatter(this: any) {
            return formatEnergyValue(this.y, chartUnit);
          },
        },
        point: {
          events: {
            click: function () {
              if (drillSelection.bucket === 'hour') return;
              const point = combinedEnergyPoints[this.index];
              if (!point) return;
              productionPointClickRef.current(point);
            },
          },
        },
      },
      series: {
        animation: { duration: 350 },
        states: { inactive: { opacity: 0.45 } },
      },
    },
    series: [
      {
        type: 'column',
        name: 'GES Üretimi',
        color: '#2563eb',
        data: combinedEnergyPoints.map((row) => row.production),
        dataLabels: {
          enabled: true,
        },
      },
      {
        type: 'column',
        name: 'Şebekeye Verilen',
        color: '#10b981',
        data: combinedEnergyPoints.map((row) => row.outgoing),
        dataLabels: {
          enabled: true,
        },
      },
      {
        type: 'column',
        name: 'Öz Tüketim',
        color: '#f59e0b',
        data: combinedEnergyPoints.map((row) => row.selfConsumption),
        dataLabels: {
          enabled: true,
        },
      },
      {
        type: 'column',
        name: 'Net',
        color: '#0891b2',
        data: combinedEnergyPoints.map((row) => row.net),
        dataLabels: {
          enabled: true,
        },
      },
    ],
  } satisfies Highcharts.Options), [combinedEnergyPoints, drillSelection.bucket, chartUnit]);

  return (
    <div className="flex h-full flex-col gap-3 p-3">

      {!loading&&uuid_id!=='new'&&!selectedSite&&<p role="alert" className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800">Saha bulunamadı veya saha bilgisi yüklenemedi.</p>}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {selectedSite&&<>
            <p className="mt-1 text-sm text-slate-500">{selectedSite.company_name||'Şirket belirtilmemiş'}{selectedSite.description?` · ${selectedSite.description}`:''}</p>
            <div className="mt-2 flex items-center gap-2"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${selectedSite.is_active?'bg-emerald-50 text-emerald-700':'bg-slate-100 text-slate-500'}`}>{selectedSite.is_active?'Aktif':'Pasif'}</span><span className="text-xs text-slate-500">{selectedSite.plant_count} santral</span></div>
          </>}
        </div>
        {headerActionsTarget && selectedSite && selectedSite.uuid_id === uuid_id && createPortal(
          <div className="flex flex-shrink-0 items-center justify-end gap-3">
            <DropdownMenu title="İşlemler" align="right" items={[
              {label:'Düzenle',icon:<Settings size={14}/>,variant:'info',onClick:()=>{setFormData(selectedSite);setModalTab('general');setSitePlants([]);setAvailablePlants([]);setIsModalOpen(true);}},
              {label:'GridBox Veri Takvimi',icon:<CalendarDays size={14}/>,variant:'success',onClick:()=>setCoverageOpen(true)},
              {label:'Sil',icon:<Trash2 size={14}/>,variant:'danger',onClick:()=>void handleDeleteSite(selectedSite)},
            ]}/>
          </div>,
          headerActionsTarget,
        )}
        {coverageOpen && selectedSite && <GridboxCoverageModal key={selectedSite.uuid_id} open onClose={()=>setCoverageOpen(false)} siteUuid={selectedSite.uuid_id} siteName={selectedSite.name}/>}
      </div>

      <div className="min-w-0 rounded-xl border-2 border-slate-300 bg-white shadow-sm">
        <div role="tablist" aria-label="Saha detay bölümleri" className="flex gap-1 overflow-x-auto rounded-t-[10px] border-b border-slate-300 bg-slate-50 p-1">
          {[
            { id: 'osos', label: 'OSOS', Icon: Database, disabled: false },
            { id: 'ekk', label: 'EKK', Icon: Radio, disabled: !selectedSite?.is_active || !selectedSite.ekk_device_count },
            { id: 'meter', label: 'Power Meter', Icon: Gauge, disabled: !selectedSite?.is_active || !selectedSite.meter_count },
          ].map(({ id, label, Icon, disabled }) => <button key={id} id={`site-tab-${id}`} role="tab" type="button"
            aria-selected={detailTab === id} aria-controls={`site-panel-${id}`} tabIndex={detailTab === id ? 0 : -1}
            disabled={disabled} title={disabled ? `${label} tanımı yok veya saha pasif` : label}
            onClick={() => setDetailTab(id)}
            onKeyDown={event => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const tabs = Array.from(event.currentTarget.parentElement!.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)'));
              const index = tabs.indexOf(event.currentTarget);
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
              tabs[next].focus(); tabs[next].click();
            }}
            className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 ${detailTab === id ? 'border-blue-600 bg-blue-600 text-white shadow-sm' : 'border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}
          ><Icon size={14} />{label}</button>)}
        </div>
        <div key={`${selectedSite?.uuid_id}:${detailTab}`} role="tabpanel" id={`site-panel-${detailTab}`} aria-labelledby={`site-tab-${detailTab}`} tabIndex={0} className="min-w-0 rounded-b-[10px]">
        {detailTab === 'ekk' && selectedSite && <EkkPanel key={`${selectedSite.uuid_id}:${ekkConfigRevision}`} site={selectedSite} collapsible={false} />}
        {detailTab === 'meter' && selectedSite && <PowerMeterPanel key={selectedSite.uuid_id} ownerUuid={selectedSite.uuid_id} scope="site" collapsible={false} />}

        {detailTab === 'osos' && <div className="min-h-0 bg-white">
          {selectedSite ? (
            <div className="flex h-full min-h-0 flex-col">
              <div className="border-b border-slate-200 bg-white px-5 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-bold text-slate-900">{selectedSite.name}</h2>
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                    {selectedSite.plant_count} santral
                  </span>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                    {selectedSite.company_name || 'Şirket belirtilmemiş'}
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${selectedSite.is_active
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'}`}
                  >
                    {selectedSite.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-5">
                {selectedSite.is_active ? (
                  <div className="space-y-6">

                  <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 grid grid-cols-1 gap-3">
                      <div className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-600">Endeks Kayıtları (Tüketim)</div>
                      <div className="tables-grid ag-theme-quartz h-[280px] w-full overflow-hidden rounded-2xl border border-slate-100">
                        <AgGridReact<GridboxEndexRow>
                          rowData={gridboxEndexRows}
                          columnDefs={endexColDefs}
                          defaultColDef={gridboxDefaultColDef}
                          pagination
                          paginationPageSize={endexPageSize}
                          paginationPageSizeSelector={[25, 50, 100, 500]}
                          suppressCellFocus
                          modules={enterpriseModules}
                          theme={gridTheme}
                          overlayNoRowsTemplate="Kayıt bulunamadı"
                        />
                      </div>

                      <div className="rounded-2xl border border-sky-100 bg-sky-50 px-3 py-2 text-xs font-bold uppercase tracking-wider text-sky-700">
                        Fatura Endeks Kayıtları
                      </div>
                      <div className="tables-grid ag-theme-quartz h-[280px] w-full overflow-hidden rounded-2xl border border-sky-100">
                        <AgGridReact<GridboxEndexRow>
                          rowData={gridboxBillingEndexRows}
                          columnDefs={endexColDefs}
                          defaultColDef={gridboxDefaultColDef}
                          pagination
                          paginationPageSize={endexPageSize}
                          paginationPageSizeSelector={[25, 50, 100, 500]}
                          suppressCellFocus
                          modules={enterpriseModules}
                          theme={gridTheme}
                          overlayNoRowsTemplate="Kayıt bulunamadı"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 lg:flex-row lg:items-end lg:justify-between">
                      <SectionTitle
                        icon={<BarChart3 size={18} />}
                        title="Enerji Zaman Serisi"
                        description="Yıllık görünümden başlayın, grafikteki bir veri noktasına tıklayarak aylık, günlük ve saatlik kırılıma inin."
                      />
                      <div className="flex flex-col items-start gap-2 text-xs">
                        <button
                          type="button"
                          onClick={() => void handleGridboxRefresh()}
                          disabled={gridboxRefreshLoading || gridboxArchiveLoading || productionSeriesLoading || !selectedSite.api_config?.gridbox?.wiringNo}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          title="GridBox verilerini yenile"
                        >
                          <RefreshCw size={14} className={gridboxRefreshLoading ? 'animate-spin' : ''} />
                        </button>
                        <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-slate-600">
                          <span className="font-semibold text-slate-800">{formatDrillLabel(drillSelection)}</span>
                          <span className="text-slate-300">•</span>
                          <span className="font-semibold text-slate-700">{tableUnit}</span>
                          <span className="text-slate-300">•</span>
                          <span>Grafikte bir veri noktasına tıklayın</span>
                        </div>
                        {drillSelection.bucket !== 'year' && (
                          <button
                            type="button"
                            onClick={() => void handleDrillBack()}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-semibold text-slate-600 transition hover:bg-slate-50"
                          >
                            Üst Kırılıma Dön
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-5 overflow-hidden rounded-2xl border border-slate-100">
                      <div className="tables-grid ag-theme-quartz h-[420px] w-full">
                        <AgGridReact<EnergySeriesPoint>
                          ref={gridRef}
                          rowData={productionSeriesLoading || gridboxArchiveLoading ? [] : combinedEnergyPoints}
                          columnDefs={combinedEnergyColDefs}
                          defaultColDef={gridboxDefaultColDef}
                          getRowId={({ data }) => data.key}
                          animateRows={false}
                          suppressCellFocus
                          modules={enterpriseModules}
                          theme={gridTheme}
                          onGridReady={handleGridReady}
                          onCellClicked={handleTableCellClick}
                          sideBar={{
                            toolPanels: [
                              { id: 'columns', labelDefault: 'Sütunlar', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
                              { id: 'filters', labelDefault: 'Filtreler', labelKey: 'filters', iconKey: 'filter', toolPanel: 'agFiltersToolPanel' },
                            ],
                          }}
                          pivotPanelShow="onlyWhenPivoting"
                          overlayNoRowsTemplate={productionSeriesLoading || gridboxArchiveLoading
                            ? '<span class="text-slate-400">Veri yükleniyor...</span>'
                            : '<span class="text-slate-400">Seçili kırılım için kayıt bulunamadı.</span>'}
                        />
                      </div>
                    </div>

                    <div className="mt-5">
                      <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                        <div className="mb-3 flex items-center justify-between">
                          <div>
                            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Grafik</div>
                            <div className="text-sm font-semibold text-slate-800">{formatDrillLabel(drillSelection)} görünüm</div>
                            <div className="mt-1 text-[11px] text-slate-500">
                              {combinedEnergyPoints.length.toLocaleString('tr-TR')} bar
                              {seriesStartLabel && seriesEndLabel
                                ? ` · ${seriesStartLabel} - ${seriesEndLabel}`
                                : ''}
                              <span className="ml-2 text-slate-400">({chartUnit})</span>
                            </div>
                          </div>
                        </div>
                        <div className="w-full">
                          <HighchartsColumnChart options={highchartsOptions} height={320} />
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                    <div className="text-sm font-semibold">Bu saha pasif durumda.</div>
                    <div className="mt-1 text-xs">GridBox yenileme, endeks arşivi ve enerji zaman serisi pasif sahalarda görüntülenmez.</div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center p-12 text-slate-400">
              Bir saha seçin.
            </div>
          )}
        </div>}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div className="flex h-[680px] w-full max-w-3xl flex-col rounded-3xl bg-white p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{formData.uuid_id ? 'Sahayı Düzenle' : 'Yeni Saha Oluştur'}</h3>
                <p className="mt-1 text-sm text-slate-500">Saha bilgilerini sekmeler arasında düzenleyin.</p>
              </div>
              <button
                type="button"
                onClick={closeSiteModal}
                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-50"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mb-4 flex gap-1 overflow-x-auto border-b border-slate-200">
              {([
                { id: 'general', label: 'Genel', icon: <Settings size={14} /> },
                { id: 'connection', label: 'Bağlantı', icon: <Plug size={14} /> },
                { id: 'plants', label: 'Santraller', icon: <Factory size={14} /> },
                { id: 'ekk', label: 'EKK', icon: <Radio size={14} /> },
              ] as const).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setModalTab(t.id)}
                  disabled={(t.id === 'plants'||t.id==='ekk') && !formData.uuid_id}
                  className={`inline-flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-bold transition disabled:cursor-not-allowed disabled:opacity-40 ${
                    modalTab === t.id
                      ? 'border-blue-600 text-blue-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                  title={(t.id === 'plants'||t.id==='ekk') && !formData.uuid_id ? 'Önce sahayı kaydedin' : undefined}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleCreateSite} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
              {modalTab==='ekk'&&formData.uuid_id&&<SiteEkkSettings siteUuid={formData.uuid_id} onSaved={()=>{
                setEkkConfigRevision(value=>value+1);
                void fetchSites().then(updated=>{setSelectedSite(current=>current?updated.find(site=>site.uuid_id===current.uuid_id)||current:null);})
                  .catch(()=>toast.error('EKK kaydedildi ancak saha listesi yenilenemedi.'));
              }}/>}
              {modalTab === 'general' && (<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-bold text-slate-700">Saha Adı</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Sahanın kayıtlı adı"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-bold text-slate-700">Açıklama</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Sahayı ayırt edecek kısa not"
                  className="h-24 w-full resize-none rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700">Şirket Adı</label>
                <input
                  type="text"
                  value={formData.company_name || ''}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Sahayı yöneten şirket adı"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700">Durum</label>
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, is_active: true })}
                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${formData.is_active === false
                      ? 'text-slate-600 hover:bg-slate-50'
                      : 'bg-emerald-50 text-emerald-700'}`}
                  >
                    <span className={`h-3 w-3 rounded-full border ${formData.is_active === false ? 'border-slate-300' : 'border-emerald-600 bg-emerald-600'}`} />
                    Aktif
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, is_active: false })}
                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${formData.is_active === false
                      ? 'bg-amber-100 text-amber-700'
                      : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    <span className={`h-3 w-3 rounded-full border ${formData.is_active === false ? 'border-amber-600 bg-amber-600' : 'border-slate-300'}`} />
                    Pasif
                  </button>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-bold text-slate-700">GridBox Tesisat No</label>
                <input
                  type="text"
                  value={formData.api_config?.gridbox?.wiringNo || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    api_config: {
                      ...(formData.api_config || {}),
                      gridbox: {
                        ...(formData.api_config?.gridbox || {}),
                        wiringNo: e.target.value,
                      },
                    },
                  })}
                  placeholder="Sahaya tanımlı tesisat numarası"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              </div>)}

              {modalTab === 'connection' && (
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Database size={16} className="text-slate-500" />
                  <h4 className="text-sm font-bold text-slate-800">GridBox Bağlantı Bilgileri</h4>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">GridBox URL</label>
                    <input
                      type="text"
                      value={formData.api_config?.gridbox?.url || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        api_config: {
                          ...(formData.api_config || {}),
                          gridbox: {
                            ...(formData.api_config?.gridbox || {}),
                            url: e.target.value,
                          },
                        },
                      })}
                      placeholder="GridBox servis adresi"
                      className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">GridBox Kullanıcı Adı</label>
                    <input
                      type="text"
                      value={formData.api_config?.gridbox?.username || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        api_config: {
                          ...(formData.api_config || {}),
                          gridbox: {
                            ...(formData.api_config?.gridbox || {}),
                            username: e.target.value,
                          },
                        },
                      })}
                      placeholder="GridBox API kullanıcı adı"
                      className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">GridBox Şifre</label>
                    <input
                      type="password"
                      value={formData.api_config?.gridbox?.password || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        api_config: {
                          ...(formData.api_config || {}),
                          gridbox: {
                            ...(formData.api_config?.gridbox || {}),
                            password: e.target.value,
                          },
                        },
                      })}
                      placeholder="GridBox API şifresi"
                      className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                  <div className="flex flex-col items-stretch justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => void handleGridboxTest()}
                      disabled={gridboxTestLoading || !formData.uuid_id}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      title={!formData.uuid_id ? 'Önce sahayı kaydedin' : 'GridBox bağlantısını test et'}
                    >
                      <RefreshCw size={14} className={gridboxTestLoading ? 'animate-spin' : ''} />
                      {gridboxTestLoading ? 'Test ediliyor...' : 'Bağlantıyı Test Et'}
                    </button>
                    {gridboxTestResult && (
                      <div
                        className={`rounded-xl px-3 py-2 text-xs ${
                          gridboxTestResult.ok
                            ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border border-rose-200 bg-rose-50 text-rose-700'
                        }`}
                      >
                        <div className="mb-2 font-semibold">{gridboxTestResult.message}</div>
                        <div className="space-y-1.5">
                          {[
                            { key: 'token', label: 'Token Alma', value: gridboxTestResult.checks.token },
                            { key: 'endex', label: 'Endeks Kaydı', value: gridboxTestResult.checks.endex },
                            { key: 'billingEndex', label: 'Fatura Kaydı', value: gridboxTestResult.checks.billingEndex },
                            { key: 'loadProfile', label: 'Profile Verisi', value: gridboxTestResult.checks.loadProfile },
                          ].map((item) => (
                            <div key={item.key} className="flex items-center justify-between gap-2 rounded-lg bg-white/60 px-2 py-1">
                              <span className="font-medium">{item.label}</span>
                              <span
                                className="inline-flex items-center gap-1"
                                title={item.value.ok ? undefined : item.value.message}
                              >
                                {item.value.ok ? <CheckCircle2 size={14} className="text-emerald-600" /> : <XCircle size={14} className="text-rose-600" />}
                                <span>{item.value.ok ? 'Kontrol Başarılı' : 'Başarısız'}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {!formData.uuid_id && (
                      <div className="text-xs text-slate-500">
                        Test için önce sahayı kaydedin.
                      </div>
                    )}
                  </div>
                </div>
              </div>
              )}

              {modalTab === 'plants' && (
              <div className="space-y-4">
                {!formData.uuid_id ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                    Önce sahayı kaydedin, sonra santral atayabilirsiniz.
                  </div>
                ) : plantsLoading ? (
                  <div className="flex items-center justify-center py-10 text-sm text-slate-500">
                    <RefreshCw size={14} className="mr-2 animate-spin" /> Yükleniyor...
                  </div>
                ) : (
                  <>
                    <div>
                      <h4 className="mb-2 text-sm font-bold text-slate-800">Atanmış Santraller ({sitePlants.length})</h4>
                      {sitePlants.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-500">
                          Bu sahaya henüz santral atanmamış.
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          {sitePlants.map((p) => (
                            <div key={p.uuid_id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-3 py-2">
                              <div className="flex items-center gap-2">
                                <Factory size={14} className="text-slate-400" />
                                <span className="text-sm font-semibold text-slate-800">{p.name}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => void handleRemovePlantFromSite(p.uuid_id)}
                                disabled={plantOpLoading}
                                className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40"
                                title="Bu sahadan çıkar"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <h4 className="mb-2 text-sm font-bold text-slate-800">Santral Ekle</h4>
                      <div className="flex items-center gap-2">
                        <select
                          value={plantToAdd}
                          onChange={(e) => setPlantToAdd(e.target.value)}
                          className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                        >
                          <option value="">— Atanabilir santral seçin —</option>
                          {availablePlants.map((p) => (
                            <option key={p.uuid_id} value={p.uuid_id}>{p.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => void handleAddPlantToSite()}
                          disabled={!plantToAdd || plantOpLoading}
                          className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-3 py-2 text-sm font-bold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Plus size={14} /> Ekle
                        </button>
                      </div>
                      {availablePlants.length === 0 && (
                        <p className="mt-1 text-[11px] text-slate-500">Atanabilir başka santral kalmadı.</p>
                      )}
                    </div>
                  </>
                )}
              </div>
              )}

              </div>
              <div className="mt-4 flex flex-shrink-0 items-center gap-3 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={closeSiteModal}
                  className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50"
                >
                  {modalTab === 'plants'||modalTab==='ekk' ? 'Kapat' : 'İptal'}
                </button>
                {modalTab !== 'plants' && modalTab!=='ekk' && (
                  <button
                    type="submit"
                    className="flex-1 rounded-xl bg-blue-600 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700"
                  >
                    {formData.uuid_id ? 'Güncelle' : 'Oluştur'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
